"use client";

/**
 * A street-address field that suggests real addresses as you type, and fills
 * city / state / ZIP / country when you pick one.
 *
 * ── FOCUS STABILITY IS THE POINT ───────────────────────────────────────────
 * The single most likely way to ship this broken is to make the field unusable
 * while it is helping. So, deliberately:
 *   - the input's `disabled` / `readOnly` are NEVER bound to `isSearching`.
 *     Disabling a focused element blurs it per the HTML spec, which drops every
 *     character typed after the flip. Progress shows as a status line beside
 *     the label instead.
 *   - the input is rendered by ONE unconditional JSX element with a stable key,
 *     never swapped between branches, so arriving suggestions re-render it in
 *     place rather than remounting it.
 *   - suggestions render in normal flow BELOW the field, not in an overlay.
 *     An absolutely-positioned dropdown inside a scrolling modal clips; inline
 *     cannot be clipped, needs no z-index token, and cannot render off-screen.
 *
 * ── WHAT IT WRITES ─────────────────────────────────────────────────────────
 * Exactly the five CRM columns, via `toCrmAddressFields` on the server. No
 * hidden `place_id` / lat / lng inputs: a required-ish value with no visible
 * representation is a trap the user could never see or correct. Every field
 * this fills stays editable.
 *
 * ── UI PRIMITIVES ──────────────────────────────────────────────────────────
 * `Input`/`Label` come from `@west-tennessee-consulting/ui` — the shared
 * design system — rather than a host app's own local copy, so this component
 * has no host-app import of its own.
 */

import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { Input, Label } from "@west-tennessee-consulting/ui";

import type { CrmAddressFields } from "../canonical.js";
import { useAddressAutocomplete } from "./use-address-autocomplete.js";
import { useAddressLookupAvailable } from "./use-address-lookup-available.js";

export interface AddressAutocompleteInputProps {
  id: string;
  /**
   * `null` when the caller already renders a label for this `id` (e.g. a
   * generic form's own `FieldWrapper`). Two `<label for>` elements pointing at
   * one input is an accessibility defect, not a cosmetic one.
   */
  label?: string | null;
  value: string;
  placeholder?: string;
  /** Paints the error border. Never gates typing — see the docblock. */
  hasError?: boolean;
  /** Every keystroke. The field stays a plain editable street-address input. */
  onValueChange: (value: string) => void;
  /** A suggestion was picked; write all five columns. */
  onAddressSelected: (fields: CrmAddressFields) => void;
}

export const AddressAutocompleteInput: React.FC<AddressAutocompleteInputProps> = ({
  id,
  label = "Street Address",
  value,
  placeholder = "123 Main Street",
  hasError = false,
  onValueChange,
  onAddressSelected,
}) => {
  const isAvailable = useAddressLookupAvailable();
  const { suggestions, isSearching, hasFailed, search, select, clear } =
    useAddressAutocomplete();
  const [isPicking, setIsPicking] = useState(false);
  const listboxId = useId();
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = event.target.value;
      onValueChange(next);
      if (isAvailable) search(next);
    },
    [isAvailable, onValueChange, search]
  );

  // Started work must be stoppable: the blur timer below is armed on every
  // blur, and without this it outlives the component and fires `clear()` on a
  // dead tree.
  useEffect(
    () => () => {
      if (blurTimerRef.current) {
        clearTimeout(blurTimerRef.current);
        blurTimerRef.current = null;
      }
    },
    []
  );

  const handleSelect = useCallback(
    async (placeId: string) => {
      setIsPicking(true);
      try {
        const fields = await select(placeId);
        if (fields) onAddressSelected(fields);
      } finally {
        // Always clears — a stuck "filling…" line would be permanent.
        setIsPicking(false);
      }
    },
    [onAddressSelected, select]
  );

  const showSuggestions = isAvailable && suggestions.length > 0;

  const status = (() => {
    if (!isAvailable) return null;
    if (isPicking) return "Filling address…";
    if (isSearching) return "Searching…";
    if (hasFailed) return "Address lookup unavailable — type the address manually.";
    return null;
  })();

  return (
    <div className="space-y-2">
      {(label !== null || status) && (
        <div className="flex items-baseline justify-between gap-2">
          {label !== null ? <Label htmlFor={id}>{label}</Label> : <span />}
          {status && (
            <span className="text-xs text-muted-foreground" aria-live="polite">
              {status}
            </span>
          )}
        </div>
      )}

      {/*
        ONE unconditional input. Never disabled on a background flag, never
        branched between two elements — both would cost the user their place.
      */}
      <Input
        id={id}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        aria-invalid={hasError || undefined}
        className={
          hasError ? "border-red-500 border-2 focus:ring-red-500 focus:border-red-500" : ""
        }
        role={isAvailable ? "combobox" : undefined}
        aria-expanded={isAvailable ? showSuggestions : undefined}
        aria-controls={showSuggestions ? listboxId : undefined}
        aria-autocomplete={isAvailable ? "list" : undefined}
        onChange={handleChange}
        onKeyDown={(event) => {
          if (event.key === "Escape" && showSuggestions) {
            event.preventDefault();
            clear();
          }
        }}
        onBlur={() => {
          // Let a click on a suggestion land before the list disappears.
          blurTimerRef.current = setTimeout(clear, 150);
        }}
        onFocus={() => {
          if (blurTimerRef.current) {
            clearTimeout(blurTimerRef.current);
            blurTimerRef.current = null;
          }
        }}
      />

      {showSuggestions && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label="Address suggestions"
          className="rounded-md border border-border divide-y divide-border overflow-hidden"
        >
          {suggestions.map((suggestion) => (
            <li key={suggestion.placeId} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={false}
                className="w-full px-3 py-2 text-left hover:bg-accent focus:bg-accent focus:outline-none"
                // mousedown ONLY holds the focus — it does not select. Keeping
                // focus on the input means the blur timer never arms, so the
                // list is still mounted when `click` lands. Selecting here too
                // would fire the pick TWICE (mousedown then click) and bill two
                // Place Details events per address; and selecting here INSTEAD
                // of on click would strand keyboard users, who reach the option
                // with Tab and activate it with Enter — which raises `click`
                // and no `mousedown` at all. One handler, on click.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => void handleSelect(suggestion.placeId)}
              >
                <span className="block text-sm">{suggestion.primaryText}</span>
                {suggestion.secondaryText && (
                  <span className="block text-xs text-muted-foreground">
                    {suggestion.secondaryText}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
